import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import { buildCanonical, buildSeoTitle } from "@/lib/seoTitle";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  noIndex?: boolean;
  /** Only applies with noIndex; defaults to true ("noindex, nofollow"). */
  noFollow?: boolean;
  structuredData?: object;
  ogImageWidth?: string;
  ogImageHeight?: string;
}

/** Language alternates per page. Add a locale here once an English variant exists. */
const HREFLANGS = ["nl-NL", "x-default"] as const;

const SEOHead = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = "website",
  noIndex = false,
  noFollow = true,
  structuredData,
  ogImageWidth = "1200",
  ogImageHeight = "630",
}: SEOHeadProps) => {
  const { data: publicSettings } = usePublicSettings<{ og_image_url?: string }>();
  const dynamicOgImage = publicSettings?.og_image_url ?? "";
  const { pathname } = useLocation();

  const fullTitle = buildSeoTitle(title);
  // Without an explicit canonical, use the current path (no query/hash) — never the homepage.
  const canonical = buildCanonical(canonicalUrl, pathname);
  const finalOgImage = ogImage || dynamicOgImage;

  useEffect(() => {
    document.title = fullTitle;

    // Build-time prerendered JSON-LD is replaced by the live version below.
    document.querySelectorAll("script[data-prerender-ld]").forEach((el) => el.remove());

    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    const updateLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    const updateHreflang = (lang: string, href: string) => {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    updateMeta("description", description);
    updateMeta("robots", noIndex ? (noFollow ? "noindex, nofollow" : "noindex, follow") : "index, follow");

    updateMeta("og:title", fullTitle, true);
    updateMeta("og:description", description, true);
    updateMeta("og:type", ogType, true);
    updateMeta("og:url", canonical, true);
    updateMeta("og:image", finalOgImage, true);
    updateMeta("og:image:width", ogImageWidth, true);
    updateMeta("og:image:height", ogImageHeight, true);
    updateMeta("og:locale", "nl_NL", true);
    updateMeta("og:site_name", "Perfecte Zorgmatch", true);

    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", fullTitle);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", finalOgImage);

    updateLink("image_src", finalOgImage);
    updateLink("canonical", canonical);
    HREFLANGS.forEach((lang) => updateHreflang(lang, canonical));

    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-structured-data]');
      if (existingScript) existingScript.remove();

      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-structured-data", "true");
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);

      return () => {
        script.remove();
      };
    }
  }, [fullTitle, description, canonical, finalOgImage, ogType, noIndex, noFollow, structuredData, ogImageWidth, ogImageHeight]);

  return null;
};

export default SEOHead;
